import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { DROP_CAP_INVALID_VALUE_MESSAGE, type DropCapFieldValidation } from '@/lib/text/drop-cap'

interface DropCapNumberFieldOptions {
  /** The value a fresh field starts at, and what a Mixed selection pins the slider to. */
  defaultValue: number
  /** The shared minimum a disabled field displays instead of its committed value. */
  displayFloor: number
  validate: (raw: string) => DropCapFieldValidation
  rangeMessage: string
  toUpdate: (value: number) => TextRunStyleUpdate
  /** Whether any selected paragraph has a drop cap; the field displays its floor while false. */
  dropCap: boolean
  emitStyleUpdate: (update: TextRunStyleUpdate) => void
  restoreActiveEditor: () => void
}

/** One Drop Cap numeric field (Lines or Offset): slider, input, validation and Esc revert. */
export function useDropCapNumberField({
  defaultValue,
  displayFloor,
  validate,
  rangeMessage,
  toUpdate,
  dropCap,
  emitStyleUpdate,
  restoreActiveEditor,
}: DropCapNumberFieldOptions) {
  const [value, setValue] = useState([defaultValue])
  const [inputValue, setInputValue] = useState(String(defaultValue))
  const [mixed, setMixed] = useState(false)
  const [errorKind, setErrorKind] = useState<'range' | 'invalid' | null>(null)
  // Same purpose as `fontSizeInputFocused`: prevent the caret-move resync effect from clobbering
  // an in-progress edit or a validation error the user just triggered.
  const inputFocused = useRef(false)
  // Snapshot of the value at the moment the field was focused — NOT the same as `value[0]`
  // itself, which onChange already overwrites in real time as the user types a valid value. Esc
  // needs the value from BEFORE this edit session started, to actually undo a real-time-applied
  // in-progress edit rather than just re-confirming it.
  const valueAtFocusRef = useRef(defaultValue)
  // Set while Esc is reverting, so the blur it triggers doesn't also run the commit logic in
  // `handleBlur`: that runs against this render's stale `inputValue` and `value`, and would
  // overwrite the value Esc just restored with the one typed mid-edit.
  const revertingRef = useRef(false)

  // A disabled field shows the shared minimum (1), not whatever value is actually committed
  // underneath — `value`/`inputValue` are left untouched by this, so turning the switch back on
  // restores the paragraph's own last-used values (or, on a paragraph that has never had a drop
  // cap, the 1/1 default those states themselves start at — for both Lines and Offset, the default
  // and this disabled-display floor happen to be the same number (1), for otherwise unrelated
  // reasons).
  const displayedValue = dropCap ? value : [displayFloor]
  const displayedInputValue = dropCap ? inputValue : String(displayFloor)
  const errorMessage = errorKind === 'invalid' ? DROP_CAP_INVALID_VALUE_MESSAGE : rangeMessage

  /** Shows and applies `next` as the committed value (slider drag, offset preset). */
  const commitValue = (next: number) => {
    setValue([next])
    setInputValue(String(next))
    setMixed(false)
    setErrorKind(null)
    emitStyleUpdate(toUpdate(next))
  }

  const handleSliderChange = (val: number[]) => commitValue(val[0])

  const handleFocus = () => {
    inputFocused.current = true
    valueAtFocusRef.current = value[0]
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    setInputValue(raw)
    setMixed(false)

    const validation = validate(raw)

    if (validation.kind === 'valid') {
      setErrorKind(null)
      setValue([validation.value])
      emitStyleUpdate(toUpdate(validation.value))
    } else if (validation.kind === 'range') {
      // A real number, just outside the field's range — clamp and apply it immediately
      // rather than leaving the out-of-range entry sitting there unapplied;
      // the range message stays up (see `handleBlur`) to explain why the
      // field snapped to the bound.
      setErrorKind('range')
      setValue([validation.clampedValue])
      setInputValue(String(validation.clampedValue))
      emitStyleUpdate(toUpdate(validation.clampedValue))
    } else {
      setErrorKind('invalid')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Enter commits and leaves focus in the field, so it starts a new edit session: a later Esc
      // must restore this value, not the one the field had when it first took focus. The value is
      // already applied (`handleChange` applies valid input as it is typed); only a clean, valid
      // entry counts as committed.
      if (errorKind == null && inputValue.trim() !== '') valueAtFocusRef.current = value[0]
      return
    }

    if (e.key !== 'Escape') return

    // Local revert only — must never reach the window-level Escape listener
    // (shape-manager/hooks/use-shape-text-editing.ts) that exits the whole text-editing session, or this
    // becomes "back out of editing" instead of "undo this field's edit".
    e.preventDefault()
    e.stopPropagation()

    // Release the "edit pending" guard BEFORE blurring: `blur()` runs `handleBlur` synchronously,
    // against this render's stale `errorKind`, which would otherwise see the error we are clearing
    // below and leave the guard set — freezing the read-model sync for every later selection.
    inputFocused.current = false

    const revertValue = valueAtFocusRef.current

    // Real-time onChange may already have pushed an in-progress valid value to
    // the paragraph — only re-emit when that actually happened, so discarding an
    // edit that was invalid the whole time doesn't write a no-op history step.
    if (value[0] !== revertValue) emitStyleUpdate(toUpdate(revertValue))

    setValue([revertValue])
    setInputValue(String(revertValue))
    setErrorKind(null)
    revertingRef.current = true
    try {
      ;(e.target as HTMLInputElement).blur()
    } finally {
      revertingRef.current = false
    }
  }

  const handleBlur = (e?: React.FocusEvent<HTMLInputElement>) => {
    if (!revertingRef.current) {
      // Only an EMPTY field silently reverts on commit — a genuinely
      // unparseable non-empty value, or one that was out of range (even though
      // it's already been clamped and applied — see `handleChange`), stays in
      // its error state so the user can see it, per the Drop Cap Error/Invalid
      // designs. `inputFocused` doubles as "there's an error still showing"
      // here, not just "the DOM input is focused" — it must stay true while one
      // is, or the read-model sync effect (`applyDropCapSummary`) clobbers
      // it away the instant `restoreActiveEditor()` below re-focuses the shape
      // text editor and fires a selectionchange. Checked via `errorKind`
      // directly rather than re-validating `inputValue`, since a clamped
      // value now reads as in-range text (e.g. "24") that would otherwise look
      // indistinguishable from one the user typed on purpose.
      if (inputValue.trim() === '') {
        inputFocused.current = false
        setInputValue(String(value[0]))
        setErrorKind(null)
      } else if (errorKind == null) {
        inputFocused.current = false
      }
    }

    // Focus moving to another control inside the Type panel (Tab, Shift+Tab, or a click on a
    // focusable control) must stay there: handing it back to the text editor would swallow the
    // keystroke's own destination. Anything else (a click on the canvas or a non-focusable spot)
    // still restores the editor as before.
    const nextFocus = e?.relatedTarget
    const movesWithinPanel =
      nextFocus instanceof Element && nextFocus.closest('[data-text-edit-keepalive]') != null

    if (!movesWithinPanel) restoreActiveEditor()
  }

  /** Turning Drop Cap off: drop the "unsynced edit pending" guard and any in-progress invalid
   *  edit, reverting the input to the last committed value. */
  const revertPendingEdit = () => {
    inputFocused.current = false
    setInputValue(String(value[0]))
    setErrorKind(null)
  }

  /** Shows a selection's drop cap value. Mixed pins the slider to the default (the floor), not to
   *  whatever the on-paragraphs happen to agree on — `summaryValue` is only meaningful once
   *  `mixedNow` is false. */
  const applySummary = useCallback(
    (mixedNow: boolean, summaryValue: number | null) => {
      if (inputFocused.current) return

      setMixed(mixedNow)
      const shown = mixedNow ? defaultValue : (summaryValue ?? defaultValue)

      setValue([shown])
      setInputValue(mixedNow ? '' : String(shown))
      setErrorKind(null)
    },
    [defaultValue]
  )

  /** Nothing selected: reset to a clean baseline. Unconditional, unlike `applySummary`: with no
   *  selection there is nothing left to correct, so a validation error (and the guard that kept it
   *  alive after blur) must not linger under the now-disabled field. */
  const reset = useCallback(() => {
    inputFocused.current = false
    setValue([defaultValue])
    setInputValue(String(defaultValue))
    setErrorKind(null)
    setMixed(false)
  }, [defaultValue])

  return {
    value,
    mixed,
    errorKind,
    errorMessage,
    displayedValue,
    displayedInputValue,
    restoreActiveEditor,
    commitValue,
    handleSliderChange,
    handleFocus,
    handleChange,
    handleKeyDown,
    handleBlur,
    revertPendingEdit,
    applySummary,
    reset,
  }
}

export type DropCapNumberField = ReturnType<typeof useDropCapNumberField>
